import React, { useEffect, useMemo, useState } from 'react';
import { useMatch, useNavigate, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';

import { ReactComponent as Logo } from 'assets/images/logo.svg';
import ALL_ROUTES from 'config/routes';
import { useAppContext } from 'contexts/AppContext';
import { useBudgetQueries } from 'utils/api/queries';
import { formatCurrency, formatPercentage } from 'utils/helper';
import { Icon } from 'utils/icons';

import './styles.scss';

const DETAIL_SHEET_KEYS = [
  'swimlane_detail_sheets',
  'swimlane_item_detail_sheets',
  'line_item_detail_sheets',
  'detail_sheets',
];

const CHILD_CATEGORY_KEYS = [
  'budget_swimlane_lineitem',
  'bs_lineitem_children',
  'children',
  'line_items',
];

const getCategoryKey = category =>
  String(
    category?.id ??
      category?.legacy_swimlane_id ??
      category?.legacy_line_item_id ??
      `${category?.name || 'category'}-${category?.position || 0}`
  );

const getDetailRows = category =>
  DETAIL_SHEET_KEYS.flatMap(key =>
    Array.isArray(category?.[key]) ? category[key] : []
  );

const getChildCategories = category =>
  CHILD_CATEGORY_KEYS.flatMap(key =>
    Array.isArray(category?.[key]) ? category[key] : []
  );

const numberValue = value => Number(value || 0);

const getCategoryTotal = category =>
  numberValue(category?.cost ?? category?.total_cost);

const getRowTotal = row =>
  numberValue(row?.column_total ?? row?.total_cost ?? row?.item_total);

const formatOptionalCurrency = (value, currency) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  return formatCurrency(value, currency);
};

const formatOptionalValue = value => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  return value;
};

const formatUnit = unit => {
  if (!unit) return '';
  if (typeof unit === 'object') return unit.name || unit.label || '';
  return unit;
};

const getFringeLabels = row => {
  const directFringes = Array.isArray(row?.fringe) ? row.fringe : [];
  const linkedFringes = Array.isArray(row?.linked_fringes)
    ? row.linked_fringes
    : [];

  return [...directFringes, ...linkedFringes]
    .map(
      fringe =>
        fringe?.display_name ||
        fringe?.label ||
        fringe?.value ||
        fringe?.link_group
    )
    .filter(Boolean)
    .join(', ');
};

const hasDetailNotes = categories =>
  categories.some(category => {
    const rows = getDetailRows(category);
    const children = getChildCategories(category);
    return (
      rows.some(row => Boolean(row?.comment)) ||
      (children.length ? hasDetailNotes(children) : false)
    );
  });

const hasDetailFringes = categories =>
  categories.some(category => {
    const rows = getDetailRows(category);
    const children = getChildCategories(category);
    return (
      rows.some(row => Boolean(getFringeLabels(row))) ||
      (children.length ? hasDetailFringes(children) : false)
    );
  });

const findCategoryPath = (categories, targetId, path = []) => {
  if (!targetId) return [];

  for (const category of categories) {
    const nextPath = [...path, category];
    if (getCategoryKey(category) === targetId) {
      return nextPath;
    }

    const childPath = findCategoryPath(
      getChildCategories(category),
      targetId,
      nextPath
    );
    if (childPath.length) {
      return childPath;
    }
  }

  return [];
};

const getFirstCategory = categories => categories[0] || null;

const getAllRows = categories =>
  categories.flatMap(category => [
    ...getDetailRows(category),
    ...getAllRows(getChildCategories(category)),
  ]);

const getCategoryColumns = (categories, selectedPath) => {
  if (!categories.length) return [];

  const columns = [
    {
      id: 'root',
      categories,
      selectedId: selectedPath[0] ? getCategoryKey(selectedPath[0]) : null,
    },
  ];

  selectedPath.forEach((category, index) => {
    const children = getChildCategories(category);
    if (!children.length) return;

    columns.push({
      id: `children-${getCategoryKey(category)}`,
      categories: children,
      selectedId: selectedPath[index + 1]
        ? getCategoryKey(selectedPath[index + 1])
        : null,
    });
  });

  return columns;
};

const getContractualChargeLabel = charge => {
  if (charge?.charge_type === 'percentage') {
    const hasPercentage =
      charge.percentage !== null &&
      charge.percentage !== undefined &&
      charge.percentage !== '';

    return hasPercentage
      ? `${charge.name} (${formatPercentage(charge.percentage)}%)`
      : charge.name;
  }

  if (charge?.charge_type === 'flat_fee') {
    return `${charge.name} (Flat Fee)`;
  }

  return charge?.name || 'Contractual Charge';
};

const CategoryColumn = ({
  column,
  currency,
  onSelect,
  contractualCharges,
  filmIncentives,
}) => (
  <div className="public-budget-category-column">
    {column.categories.map(category => {
      const categoryKey = getCategoryKey(category);
      const hasChildren = getChildCategories(category).length > 0;

      return (
        <button
          type="button"
          key={categoryKey}
          className={`public-budget-category-row ${
            column.selectedId === categoryKey ? 'is-active' : ''
          }`}
          onClick={() => onSelect(categoryKey)}
        >
          <span className="public-budget-category-name">
            {category?.name || 'Untitled Category'}
          </span>
          <span className="public-budget-category-total">
            {formatCurrency(getCategoryTotal(category), currency)}
          </span>
          {hasChildren && (
            <span className="public-budget-category-arrow" aria-hidden="true">
              <Icon name="ArrowForwardIcon" />
            </span>
          )}
        </button>
      );
    })}
    {contractualCharges.length > 0 && (
      <div className="public-budget-contractual-charges">
        <div className="public-budget-contractual-heading">
          Contractual Charges
        </div>
        {contractualCharges.map(charge => (
          <div
            key={`contractual-charge-${charge.id}`}
            className="public-budget-contractual-row"
          >
            <span className="public-budget-category-name">
              {getContractualChargeLabel(charge)}
            </span>
            <span className="public-budget-category-total">
              {formatCurrency(charge.total_cost || 0, currency)}
            </span>
          </div>
        ))}
      </div>
    )}
    {filmIncentives.length > 0 && (
      <div className="public-budget-contractual-charges">
        <div className="public-budget-contractual-heading">
          Estimated Film Tax Credits
        </div>
        {filmIncentives.map(incentive => (
          <div
            key={`film-incentive-${incentive.id}`}
            className="public-budget-contractual-row public-budget-film-incentive-row"
          >
            <span className="public-budget-category-name">
              {incentive.name} ({formatPercentage(incentive.rate_percent)}%)
            </span>
            <span className="public-budget-category-total">
              {incentive.estimate_complete
                ? `−${formatCurrency(incentive.net_credit || 0, currency)}`
                : 'Incomplete'}
            </span>
          </div>
        ))}
      </div>
    )}
  </div>
);

CategoryColumn.propTypes = {
  column: PropTypes.shape({
    categories: PropTypes.array.isRequired,
    selectedId: PropTypes.string,
  }).isRequired,
  currency: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
  contractualCharges: PropTypes.array,
  filmIncentives: PropTypes.array,
};

CategoryColumn.defaultProps = {
  contractualCharges: [],
  filmIncentives: [],
};

const DetailRows = ({
  rows,
  currency,
  showActuals,
  showFringes,
  showNotes,
}) => {
  if (!rows.length) {
    return (
      <div className="public-budget-detail-empty">
        No detail rows in this category.
      </div>
    );
  }

  return (
    <div className="public-budget-detail-grid-wrap">
      <table className="public-budget-detail-grid">
        <thead>
          <tr>
            <th>Item</th>
            <th>Qty</th>
            <th>Unit</th>
            <th>Rate</th>
            <th>Mult</th>
            {showFringes && <th>Fringes</th>}
            <th>Total</th>
            {showActuals && <th>Actual</th>}
            {showNotes && <th>Notes</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr key={`detail-row-${row.id}`}>
              <td>{row?.item || 'Untitled Item'}</td>
              <td>{formatOptionalValue(row?.quantity)}</td>
              <td>{formatUnit(row?.unit)}</td>
              <td>{formatOptionalCurrency(row?.rate, currency)}</td>
              <td>{formatOptionalValue(row?.multiplier)}</td>
              {showFringes && <td>{getFringeLabels(row)}</td>}
              <td>{formatCurrency(getRowTotal(row), currency)}</td>
              {showActuals && (
                <td>{formatOptionalCurrency(row?.actual, currency)}</td>
              )}
              {showNotes && <td>{row?.comment || ''}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

DetailRows.propTypes = {
  rows: PropTypes.array.isRequired,
  currency: PropTypes.string.isRequired,
  showActuals: PropTypes.bool.isRequired,
  showFringes: PropTypes.bool.isRequired,
  showNotes: PropTypes.bool.isRequired,
};

const PublicBudget = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const embedMatch = useMatch(ALL_ROUTES.PUBLIC_BUDGET_EMBED);
  const isEmbed = Boolean(embedMatch);
  const { isDarkMode, toggleTheme } = useAppContext();
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);

  const {
    data: publicBudgetResponse,
    isFetching,
    isError,
  } = useBudgetQueries.usePublicBudgetQuery({
    token,
    enabled: Boolean(token),
  });

  const budget = publicBudgetResponse?.data?.budget;
  const categories = useMemo(
    () =>
      Array.isArray(budget?.budget_swimlane) ? budget.budget_swimlane : [],
    [budget]
  );
  const contractualCharges = useMemo(
    () =>
      Array.isArray(budget?.contractual_charges)
        ? [...budget.contractual_charges].sort(
            (a, b) =>
              numberValue(a?.position) - numberValue(b?.position) ||
              numberValue(a?.id) - numberValue(b?.id)
          )
        : [],
    [budget]
  );
  const filmIncentives = useMemo(
    () =>
      Array.isArray(budget?.film_incentives)
        ? [...budget.film_incentives].sort(
            (a, b) =>
              numberValue(a?.position) - numberValue(b?.position) ||
              numberValue(a?.id) - numberValue(b?.id)
          )
        : [],
    [budget]
  );
  const currency = budget?.primary_currency || 'USD';
  const showActuals = Boolean(budget?.has_actuals);
  const showFringes = hasDetailFringes(categories);
  const showNotes = hasDetailNotes(categories);
  const selectedPath = useMemo(
    () => findCategoryPath(categories, selectedCategoryId),
    [categories, selectedCategoryId]
  );
  const selectedCategory = selectedPath[selectedPath.length - 1] || null;
  const selectedRows = selectedCategory ? getDetailRows(selectedCategory) : [];
  const categoryColumns = useMemo(
    () => getCategoryColumns(categories, selectedPath),
    [categories, selectedPath]
  );
  const allRows = useMemo(() => getAllRows(categories), [categories]);
  const visibleActualTotal = showActuals
    ? budget?.actual_total
    : allRows
        .filter(row => row?.actual !== null && row?.actual !== undefined)
        .reduce((total, row) => total + numberValue(row.actual), 0);
  useEffect(() => {
    if (budget?.name) {
      document.title = `${budget.name} | CineBudget Pro`;
    }
  }, [budget?.name]);

  useEffect(() => {
    document.body.classList.add('public-budget-mode');
    if (isEmbed) {
      document.body.classList.add('public-budget-embed-mode');
    }
    return () => {
      document.body.classList.remove('public-budget-mode');
      document.body.classList.remove('public-budget-embed-mode');
    };
  }, [isEmbed]);

  useEffect(() => {
    if (!categories.length) {
      setSelectedCategoryId(null);
      return;
    }

    if (selectedPath.length) return;

    const firstCategory = getFirstCategory(categories);
    if (firstCategory) {
      setSelectedCategoryId(getCategoryKey(firstCategory));
    }
  }, [categories, selectedPath.length]);

  const handleAuthNavigate = () => {
    navigate(ALL_ROUTES.AUTH_CHILDREN.SIGNUP);
  };

  if (isFetching) {
    return (
      <div className="public-budget-page public-budget-page-loading">
        Loading public budget...
      </div>
    );
  }

  if (isError || !budget) {
    return (
      <div className="public-budget-page public-budget-page-error">
        <button
          type="button"
          className="public-budget-logo-button"
          onClick={handleAuthNavigate}
          aria-label="CineBudget Pro"
        >
          <Logo />
        </button>
        <div className="public-budget-empty-state">
          This public budget link is not available.
        </div>
      </div>
    );
  }

  return (
    <div
      className={`public-budget-page ${
        isEmbed ? 'public-budget-page-embed' : ''
      }`}
    >
      <header className="public-budget-header">
        <button
          type="button"
          className="public-budget-logo-button"
          onClick={handleAuthNavigate}
          aria-label="CineBudget Pro"
        >
          <Logo />
        </button>

        {!isEmbed && (
          <div className="public-budget-header-title">
            <strong>{budget.name}</strong>
            <span>Public View</span>
          </div>
        )}

        <div className="public-budget-header-actions">
          <button
            type="button"
            className="public-budget-theme-toggle"
            onClick={toggleTheme}
            aria-label={
              isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'
            }
            title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            <span className={!isDarkMode ? 'is-active' : ''}>
              <Icon name="SunIcon" />
            </span>
            <span className={isDarkMode ? 'is-active' : ''}>
              <Icon name="MoonIcon" />
            </span>
          </button>
        </div>
      </header>

      <nav className="public-budget-tabs" aria-label="Public budget tabs">
        <button type="button" className="public-budget-tab is-active">
          <span className="public-budget-tab-label">{budget.name}</span>
        </button>
        {!isEmbed && (
          <button
            type="button"
            className="public-budget-tab public-budget-tab-new-budget"
            onClick={handleAuthNavigate}
          >
            <Icon name="AddIcon" />
            <span className="public-budget-tab-label">New Budget</span>
          </button>
        )}
      </nav>

      <main className="public-budget-workspace">
        <section className="public-budget-budget-panel">
          <div className="public-budget-panel-head">
            <div className="public-budget-panel-title">
              <strong>{budget.name}</strong>
            </div>
          </div>

          <div className="public-budget-content-area">
            {categories.length ? (
              <div
                className="public-budget-category-browser"
                aria-label="Budget categories"
                style={{
                  '--public-category-column-count': categoryColumns.length,
                }}
              >
                {categoryColumns.map(column => (
                  <CategoryColumn
                    key={column.id}
                    column={column}
                    currency={currency}
                    onSelect={setSelectedCategoryId}
                    contractualCharges={
                      column.id === 'root' ? contractualCharges : []
                    }
                    filmIncentives={column.id === 'root' ? filmIncentives : []}
                  />
                ))}
              </div>
            ) : (
              <div className="public-budget-category-empty">
                No categories in this budget.
              </div>
            )}

            <section className="public-budget-detail-pane">
              <div className="public-budget-detail-head">
                <div>
                  <strong>{selectedCategory?.name || 'Budget Details'}</strong>
                  {selectedPath.length > 1 && (
                    <span>
                      {selectedPath
                        .map(category => category?.name || 'Untitled Category')
                        .join(' / ')}
                    </span>
                  )}
                </div>
                {selectedCategory && (
                  <strong>
                    {formatCurrency(
                      getCategoryTotal(selectedCategory),
                      currency
                    )}
                  </strong>
                )}
              </div>

              <DetailRows
                rows={selectedRows}
                currency={currency}
                showActuals={showActuals}
                showFringes={showFringes}
                showNotes={showNotes}
              />
            </section>
          </div>
        </section>
      </main>

      <footer className="public-budget-bottom-bar">
        <div>
          <span>Budget Total</span>
          <strong>{formatCurrency(budget.total_cost, currency)}</strong>
        </div>
        {filmIncentives.length > 0 && (
          <>
            <div>
              <span>Estimated Credits</span>
              <strong>
                {budget.film_incentive_estimate_incomplete
                  ? 'Incomplete'
                  : `−${formatCurrency(
                      budget.film_incentive_total || 0,
                      currency
                    )}`}
              </strong>
            </div>
            <div>
              <span>Net Production Cost</span>
              <strong>
                {budget.net_total_cost === null ||
                budget.net_total_cost === undefined
                  ? 'Incomplete'
                  : formatCurrency(budget.net_total_cost, currency)}
              </strong>
            </div>
          </>
        )}
        {showActuals && (
          <div>
            <span>Actual Total</span>
            <strong>{formatCurrency(visibleActualTotal, currency)}</strong>
          </div>
        )}
      </footer>
    </div>
  );
};

export default PublicBudget;
